import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Button, Checkbox, IconButton, MenuItem, Stack, TextField } from "@mui/material";
import { SelectEntidade } from "@/components/SelectEntidade";
import { Add, Block, Edit, Sync } from "@mui/icons-material";
import { FilterModal } from "@/components/FilterModal";
import { SeloIntegracao } from "@/components/escala/SeloIntegracao";
import { DataField } from "@/components/DataField";
import usePagination from "@/hooks/pagination";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useNotification } from "@/context/NotificationContext";
import { useCrud } from "@/hooks/useCrud";
import { DataTable } from "@/components/DataTable";
import { CrudModal } from "@/components/crud/CrudModal";
import { CrudToolbar } from "@/components/crud/CrudToolbar";
import {
  ImportadorPlanilha,
  type ColunaImportacao,
  type LinhaImportacaoErro,
  type LinhaPlanilha,
  type ResultadoImportacao,
} from "@/components/import/ImportadorPlanilha";
import { useLogger } from "@/hooks/useLogger";
import { useListaRemota } from "@/hooks/useListaRemota";
import { useFilterState } from "@/hooks/useFilterState";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import {
  cancelarAusencia,
  formatDataBR,
  importarAusencias,
  listAusencias,
  listarTodosColaboradores,
  saveAusencia,
  AUSENCIA_TIPOS,
  AusenciaTipoLabels,
  nomeDe,
  ORIGENS_CADASTRO,
  type Ausencia,
  type AusenciaTipo,
  type Colaborador,
  type OrigemCadastro,
} from "@/api/escala.service";

// Colunas do modelo de planilha de importação de ausências — o colaborador
// aparece por matrícula ou nome (o que estiver preenchido na base), a
// resolução pra colaborador_id acontece em resolverAusenciasParaImportar
// usando a mesma lista de colaboradores já carregada pra tela.
const colunasImportacaoAusencias: ColunaImportacao[] = [
  { chave: "colaborador", rotulo: "Colaborador (Matrícula ou Nome)", obrigatoria: true, exemplo: "1001" },
  { chave: "tipo", rotulo: "Tipo (AFASTAMENTO/FERIAS/DESLIGAMENTO)", obrigatoria: true, exemplo: "AFASTAMENTO" },
  { chave: "descricao", rotulo: "Descrição", exemplo: "Atestado médico" },
  { chave: "data_inicio", rotulo: "Data de Início (DD/MM/AAAA)", rotulosAlternativos: ["Data de Início (AAAA-MM-DD)"], tipo: "data", obrigatoria: true, exemplo: "10/01/2026" },
  { chave: "data_fim", rotulo: "Data de Fim (DD/MM/AAAA) — vazio para desligamento", rotulosAlternativos: ["Data de Fim (AAAA-MM-DD) — vazio para desligamento"], tipo: "data", exemplo: "20/01/2026" },
];

// resolverAusenciasParaImportar traduz as linhas cruas da planilha (com
// colaborador por matrícula/nome) para o shape que escala.importar_ausencias
// espera (colaborador_id resolvido), mesma divisão de trabalho de
// resolverColaboradoresParaImportar em EscalaColaboradoresTab. Linha é
// 1-based, mesma convenção do Method.
function resolverAusenciasParaImportar(
  linhas: LinhaPlanilha[],
  colaboradores: Colaborador[],
): { ausencias: Ausencia[]; erros: LinhaImportacaoErro[] } {
  const erros: LinhaImportacaoErro[] = [];
  const ausencias: Ausencia[] = [];

  linhas.forEach((linha, i) => {
    const numeroLinha = i + 1;
    const identificador = (linha.colaborador ?? "").trim().toLowerCase();

    const colaborador =
      colaboradores.find((c) => (c.matricula ?? "").trim().toLowerCase() === identificador && identificador !== "") ??
      colaboradores.find((c) => c.nome.trim().toLowerCase() === identificador);
    if (!colaborador) {
      erros.push({ linha: numeroLinha, erro: `colaborador "${linha.colaborador}" não encontrado (use matrícula ou nome exato)` });
      return;
    }

    const tipo = (linha.tipo ?? "").trim().toUpperCase();
    if (tipo !== "AFASTAMENTO" && tipo !== "FERIAS" && tipo !== "DESLIGAMENTO") {
      erros.push({ linha: numeroLinha, erro: `tipo deve ser AFASTAMENTO, FERIAS ou DESLIGAMENTO, veio "${linha.tipo}"` });
      return;
    }

    ausencias.push({
      colaborador_id: colaborador.id!,
      tipo: tipo as AusenciaTipo,
      descricao: linha.descricao || undefined,
      data_inicio: linha.data_inicio ?? "",
      data_fim: tipo === "DESLIGAMENTO" ? undefined : linha.data_fim,
      cancelada: false,
    });
  });

  return { ausencias, erros };
}

interface AusenciaFormProps {
  values: Partial<Ausencia>;
  setValues: Dispatch<SetStateAction<Partial<Ausencia>>>;
  isEditing: boolean;
  colaboradores: Colaborador[];
}

function AusenciaForm({ values, setValues, colaboradores }: AusenciaFormProps) {
  const desligamento = values.tipo === "DESLIGAMENTO";

  return (
    <>
      <SelectEntidade
        label="Colaborador"
        options={colaboradores}
        value={values.colaborador_id}
        onChange={(id) => setValues((v) => ({ ...v, colaborador_id: id ?? undefined }))}
        getLabel={(c) => c.nome}
        required
      />

      <TextField
        select
        label="Tipo"
        fullWidth
        required
        value={values.tipo ?? ""}
        onChange={(e) => {
          const tipo = e.target.value as AusenciaTipo;
          setValues((v) => ({ ...v, tipo, data_fim: tipo === "DESLIGAMENTO" ? "" : v.data_fim }));
        }}
      >
        {AUSENCIA_TIPOS.map((t) => (
          <MenuItem key={t.value} value={t.value}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Descrição"
        fullWidth
        value={values.descricao ?? ""}
        onChange={(e) => setValues((v) => ({ ...v, descricao: e.target.value }))}
        slotProps={{ htmlInput: { maxLength: 120 } }}
      />

      <Stack direction="row" spacing={2}>
        <DataField
          label="Data de Início"
          fullWidth
          required
          value={values.data_inicio ?? ""}
          onChange={(data) => setValues((v) => ({ ...v, data_inicio: data }))}
        />
        <DataField
          label="Data de Fim"
          fullWidth
          required={!desligamento}
          disabled={desligamento}
          helperText={desligamento ? "Desligamento não tem data de fim" : undefined}
          value={values.data_fim ?? ""}
          onChange={(data) => setValues((v) => ({ ...v, data_fim: data }))}
        />
      </Stack>
    </>
  );
}

export function EscalaAusenciasTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Ausencia>();

  const [cancelando, setCancelando] = useState<Ausencia | null>(null);

  const { inputValues, setInputValues, filtros, applyFilters, handleClearFilters } = useFilterState(
    { colaborador_id: "", de: "", ate: "", origem: "" as OrigemCadastro | "" },
    () => setPage(0),
  );

  const { itens: colaboradores } = useListaRemota(
    () => listarTodosColaboradores({}, clientId),
    [clientId],
    "Erro ao carregar colaboradores",
  );

  const { itens: ausencias, carregouAlgumaVez, recarregar } = useListaRemota(
    () =>
      listAusencias(
        {
          colaborador_id: filtros.colaborador_id || undefined,
          de: filtros.de || undefined,
          ate: filtros.ate || undefined,
          origem: filtros.origem || undefined,
        },
        clientId,
      ),
    [clientId, filtros],
    "Erro ao carregar ausências",
  );

  const ordenadas = useMemo(
    () => [...ausencias].sort((a, b) => b.data_inicio.localeCompare(a.data_inicio)),
    [ausencias],
  );

  useEffect(() => {
    setTotal(ordenadas.length);
  }, [ordenadas, setTotal]);

  const paged = ordenadas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const activeFilterCount = Object.values(filtros).filter((v) => v).length;

  const onSave = async (values: Partial<Ausencia>) => {
    if (!values.colaborador_id) {
      notify("Colaborador é obrigatório", "error");
      return;
    }
    if (!values.tipo) {
      notify("Tipo é obrigatório", "error");
      return;
    }
    if (!values.data_inicio) {
      notify("Data de início é obrigatória", "error");
      return;
    }
    if (values.tipo !== "DESLIGAMENTO" && !values.data_fim) {
      notify("Data de fim é obrigatória", "error");
      return;
    }
    if (values.data_fim && values.data_fim < values.data_inicio) {
      notify("A data de fim não pode ser antes da data de início", "error");
      return;
    }

    try {
      const result = await saveAusencia(
        {
          id: editing?.id,
          colaborador_id: values.colaborador_id,
          tipo: values.tipo,
          descricao: values.descricao || undefined,
          data_inicio: values.data_inicio,
          data_fim: values.tipo === "DESLIGAMENTO" ? undefined : values.data_fim,
          cancelada: editing?.cancelada ?? false,
        },
        clientId,
      );

      log(
        editing ? LogEventType.Updated : LogEventType.Created,
        result.ausencia,
        "escala_ausencia",
        result.ausencia.id,
        "info",
      );
      notifyPropagacao(`Ausência ${editing ? "atualizada" : "registrada"}!`, result.dias_afetados, result.escalas_nao_alteradas);
      recarregar();
      handleClose();
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar ausência", "error");
    }
  };

  // A ausência também reescreve as escalas em rascunho; as validadas ou
  // aprovadas ficam como estão, e isso vira aviso.
  const notifyPropagacao = (base: string, dias: number | undefined, naoAlteradas: number | undefined) => {
    let mensagem = base;
    if (dias) mensagem += ` ${dias} dia(s) das escalas em rascunho foram atualizados.`;
    if (naoAlteradas) {
      mensagem += ` ${naoAlteradas} escala(s) já validada(s) ou aprovada(s) não foram alteradas.`;
    }
    notify(mensagem, naoAlteradas ? "warning" : "success");
  };

  const handleImportarAusencias = async (linhasPlanilha: LinhaPlanilha[]): Promise<ResultadoImportacao> => {
    const { ausencias, erros } = resolverAusenciasParaImportar(linhasPlanilha, colaboradores);
    if (erros.length > 0) {
      return { importados: 0, erros };
    }
    return importarAusencias(ausencias, clientId);
  };

  const onCancelarConfirm = async () => {
    if (!cancelando?.id) return;
    try {
      const result = await cancelarAusencia(cancelando.id, clientId);
      log(LogEventType.Updated, { ...cancelando, cancelada: true }, "escala_ausencia", cancelando.id, "info");
      notifyPropagacao("Ausência cancelada!", result.dias_restaurados, result.escalas_nao_alteradas);
      recarregar();
    } catch (e: any) {
      notify(e?.message || "Erro ao cancelar ausência", "error");
    } finally {
      setCancelando(null);
    }
  };

  if (!carregouAlgumaVez) return <Sync />;

  return (
    <>
      <CrudToolbar>
        <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
          <SelectEntidade
            label="Colaborador"
            options={colaboradores}
            value={inputValues.colaborador_id}
            onChange={(id) => setInputValues((v) => ({ ...v, colaborador_id: id ?? "" }))}
            getLabel={(c) => c.nome}
            size="small"
          />
          <DataField
            label="De"
            size="small"
            fullWidth
            value={inputValues.de}
            onChange={(data) => setInputValues((v) => ({ ...v, de: data }))}
          />
          <DataField
            label="Até"
            size="small"
            fullWidth
            value={inputValues.ate}
            onChange={(data) => setInputValues((v) => ({ ...v, ate: data }))}
          />
          <TextField
            select
            label="Origem"
            size="small"
            fullWidth
            value={inputValues.origem}
            onChange={(e) => setInputValues((v) => ({ ...v, origem: e.target.value as OrigemCadastro | "" }))}
          >
            <MenuItem value="">Todas</MenuItem>
            {ORIGENS_CADASTRO.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </FilterModal>
        <Stack direction="row" spacing={2}>
          <ImportadorPlanilha
            titulo="Importar ausências"
            nomeArquivoModelo="modelo_ausencias.xlsx"
            colunas={colunasImportacaoAusencias}
            onImportar={handleImportarAusencias}
            onConcluido={recarregar}
          />
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Registrar ausência
          </Button>
        </Stack>
      </CrudToolbar>

      <DataTable
        data={paged}
        columns={[
          { key: "cancelada", label: "Ativa", render: (a) => <Checkbox checked={!a.cancelada} disabled /> },
          {
            key: "colaborador_id",
            label: "Colaborador",
            render: (a) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{nomeDe(colaboradores, a.colaborador_id)}</span>
                <SeloIntegracao sistema={a.travado_por} />
              </Stack>
            ),
            exportValue: (a) => nomeDe(colaboradores, a.colaborador_id),
          },
          { key: "tipo", label: "Tipo", render: (a) => AusenciaTipoLabels[a.tipo] },
          { key: "descricao", label: "Descrição" },
          { key: "data_inicio", label: "Início", render: (a) => formatDataBR(a.data_inicio) },
          { key: "data_fim", label: "Fim", render: (a) => formatDataBR(a.data_fim) },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(ausencia) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton
              aria-label={`Editar ausência de ${nomeDe(colaboradores, ausencia.colaborador_id)}`}
              onClick={() => handleEdit(ausencia)}
              disabled={ausencia.cancelada || Boolean(ausencia.travado_por)}
            >
              <Edit />
            </IconButton>
            <IconButton
              aria-label={`Cancelar ausência de ${nomeDe(colaboradores, ausencia.colaborador_id)}`}
              onClick={() => setCancelando(ausencia)}
              disabled={ausencia.cancelada}
              color="warning"
            >
              <Block />
            </IconButton>
          </Stack>
        )}
      />

      <CrudModal<Ausencia, { colaboradores: Colaborador[] }>
        open={open}
        onClose={handleClose}
        title={editing ? "Editar ausência" : "Registrar ausência"}
        initialData={editing}
        onSubmit={onSave}
        Form={AusenciaForm}
        extraFormProps={{ colaboradores }}
      />

      <ConfirmModal
        open={!!cancelando}
        onClose={() => setCancelando(null)}
        onConfirm={onCancelarConfirm}
        title="Cancelamento"
        btnText="Confirmar"
        text={`Deseja cancelar a ausência de ${nomeDe(colaboradores, cancelando?.colaborador_id)}?`}
      />
    </>
  );
}
